<template>
  <div class="flex flex-col gap-4">
    <div
      v-for="(adapter, index) in accessories"
      :key="adapter.id"
      :data-testid="`adapterDetails-${index}`"
    >
      <div v-if="accessoryServiceList">
        <AdapterDetailsTKD
          v-model:adapter="accessories[index]"
          v-model:adapter-service="accessoryServiceList[adapter.id]"
          :remove-adapter="removeAdapter"
          :is-last-element="isLastElement(index)"
          :disabled="!isEditable(adapter)"
          :is-new-adapter="isNewlyCreatedAdapter(adapter)"
          @on-adapter-change="adapterChange"
          @add-adapter="addAdapter"
          @add-adapter-service="addAdapterService"
          @remove-adapter-service="removeAdapterService"
        />
      </div>
      <div v-else>
        <AdapterDetails
          v-model:adapter="accessories[index]"
          :remove-adapter="removeAdapter"
          :is-last-element="isLastElement(index)"
          :disabled="!isEditable(adapter)"
          @on-adapter-change="adapterChange"
        />
      </div>
    </div>

    <PlusButton
      test-id="addAdapter"
      title="Adapter hinzufügen"
      @add-element="addAdapter"
    />
  </div>
</template>
<script setup lang="ts">
import { Adapter } from '@/models/installationPoint/Accessory';
import AdapterDetails from '@/components/Forms/DeviceFormComponents/AdapterDetails.vue';
import { computed, ref } from 'vue';
import PlusButton from '@/components/UI/Button/PlusButton.vue';
import { InstallationPoint } from '@/models/installationPoint/InstallationPoint';
import { InspectionPoint } from '@/models/inspectionPoint/InspectionPoint';
import { useOrderStore } from '@/store/order/orderStore';
import {
  EntityTransactionType,
  TransactionDiff,
  TransactionDiffType,
} from '@/models/OrderChanges';
import {
  AccessoryType,
  adapterMountReasonList,
  AdapterServiceTypeEnum,
  DeviceServiceReasonEnum,
} from '@/enums/device';
import { useValidation } from '@/composables/useValidation';
import AdapterDetailsTKD from '@/components/Forms/TkdServiceComponents/AdapterDetailsTKD.vue';
import { Device } from '@/models/devices/Device';
import { AccessoryService } from '@/models/installationPoint/AccessoryService';

const model = defineModel<InstallationPoint | InspectionPoint>({
  required: true,
});
const accessoryServiceList = defineModel<Record<string, AccessoryService>>(
  'accessoryServiceList'
);

const emit = defineEmits(['onAdapterChange']);

const createdAdapters = ref<Adapter[]>([]);

const isEditable = (adapter: Adapter) => {
  if (createdAdapters.value.some((created) => created.id === adapter.id)) {
    return true;
  }
  if (accessoryServiceList.value) {
    for (const service of Object.values(accessoryServiceList.value)) {
      if (service.accessoryId === adapter.id) {
        return false;
      }

      if (service.newAccessoryId === adapter.id) {
        return service.reason !== DeviceServiceReasonEnum.DEFECT;
      }
    }
  }

  const isInspectionOrder = useOrderStore().isOnSiteInspection();
  return isInspectionOrder || isNewAccessory(adapter);
};

const accessories = computed(() => {
  return model.value.accessories.filter(
    (accessory) =>
      accessory.type !== AccessoryType.SRT_BATTERY_PACK &&
      !accessory.unmountedAt
  );
});

const addAdapter = (adapter: Adapter | undefined) => {
  if (adapter) {
    model.value.accessories.push(adapter);
    createdAdapters.value.push(adapter);
  } else {
    const adapter = new Adapter({ type: AccessoryType.SRT_ADAPTER });
    adapter.mountedAt = new Date();
    model.value.accessories.push(adapter);
    createdAdapters.value.push(adapter);

    if (accessoryServiceList.value) {
      accessoryServiceList.value[adapter.id] = new AccessoryService();
      accessoryServiceList.value[adapter.id].accessoryId = adapter.id;
      accessoryServiceList.value[adapter.id].type =
        AdapterServiceTypeEnum.MOUNT;
      accessoryServiceList.value[adapter.id].reason =
        adapterMountReasonList[0].value;
    }
  }
};

const removeAdapter = (adapter: Adapter) => {
  adapterChange(adapter);
  if (isNewAccessory(adapter)) {
    model.value.accessories = model.value.accessories.filter(
      (accessory) => accessory.id !== adapter.id
    );
    removeAdapterService({
      adapterService: accessoryServiceList.value?.[adapter.id],
      adapter: adapter,
    });
  } else {
    adapter.unmountedAt = new Date();
  }
};

const isNewlyCreatedAdapter = (adapter: Adapter) => {
  return createdAdapters.value.includes(adapter);
};

const isNewAccessory = (accessory: Adapter): boolean => {
  if (!accessory.mountedAt) return true;
  if (isNewlyCreatedAdapter(accessory)) return true;

  const entityTransactions =
    useOrderStore().getOrderChanges()?.transactions[model.value.id];
  if (!entityTransactions) return false;

  if (entityTransactions.type === EntityTransactionType.CREATE_ENTITY) {
    return true;
  }

  if (entityTransactions.type === EntityTransactionType.UPDATE_ENTITY) {
    return entityTransactions.diff.some(
      (diff: TransactionDiff) =>
        diff.type === TransactionDiffType.CREATE &&
        diff.path.includes(accessory.id)
    );
  }
  return false;
};

const isLastElement = (index) => model.value.accessories.length === index + 1;

const adapterChange = (adapter: Adapter) => {
  // re-measuring not needed for this adapter
  if (adapter.customizedNumber != '42050') {
    emit('onAdapterChange', adapter);
  }
};

const addAdapterService = (payload: {
  adapterService: AccessoryService;
  adapter: Adapter;
}) => {
  console.log('add', payload);

  const localAdapterIndex = model.value.accessories.findIndex(
    (accessory) => accessory.id === payload.adapterService.accessoryId
  );

  if (localAdapterIndex !== -1) {
    model.value.accessories.splice(localAdapterIndex + 1, 0, payload.adapter);
    createdAdapters.value.splice(localAdapterIndex + 1, 0, payload.adapter);
  } else {
    model.value.accessories.push(payload.adapter);
    createdAdapters.value.push(payload.adapter);
  }
  if (!accessoryServiceList.value) {
    accessoryServiceList.value = {};
  }
  accessoryServiceList.value[payload.adapter.id] = payload.adapterService;
};

const removeAdapterService = (payload: {
  adapterService: AccessoryService;
  adapter: Adapter;
}) => {
  console.log('removeAdapterService', payload);
  if (accessoryServiceList.value?.[payload.adapter.id]) {
    delete accessoryServiceList.value[payload.adapter.id];
  }

  const adapterIndex = model.value.accessories.findIndex(
    (accessory) => accessory.id === payload.adapter.id
  );

  const newCreatedAdapterIndex = createdAdapters.value.findIndex(
    (accessory) => accessory.id === payload.adapter.id
  );

  if (adapterIndex !== -1 && newCreatedAdapterIndex !== -1) {
    model.value.accessories.splice(adapterIndex, 1);
    createdAdapters.value.splice(newCreatedAdapterIndex, 1);
  }
};

const { isFormValid } = useValidation({ createdAdapters });

defineExpose({
  isFormValid,
});
</script>
